import React from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import { CompanyActiveIcon, CompanyInactiveIcon, DoctorActiveIcon, DoctorInactiveIcon, Download2Icon, EditIcon, LockIcon, PatientActiveIcon, PatientInactiveIcon, ShieldIcon, ViewIcon } from 'assets/icons';
import { Button, Input, Pagination } from 'components';
import { Constants, Globals } from 'utils';
import { classes } from 'utils/parse';
import { ModalApproveDoctor, ModalDisableDoctor, ModalDeletePatient, ModalRejectDoctor, ModalEnableDoctor, ModalDeleteCompany } from './modal-confirm';
import ModalViewDoctor from './modal-view-doctor';
import ModalEditDoctor from './modal-edit-doctor';
import ModalViewPatient from './modal-view-patient';
import ModalEditPatient from './modal-edit-patient';
import ModalViewCompany from './modal-view-company';
import ModalEditCompany from './modal-edit-company';
import { UsersService } from 'services';

type IonChangeEvent = React.ChangeEvent<HTMLInputElement>;

const MODAL = {
  NONE: 0,
  VIEW_PATIENT: 1,
  VIEW_DOCTOR: 2,
  VIEW_COMPANY: 3,
  EDIT_PATIENT: 4,
  EDIT_DOCTOR: 5,
  EDIT_COMPANY: 6,
  ACCEPT_DOCTOR: 7,
  REJECT_DOCTOR: 8,
  ENABLE_DOCTOR: 9,
  DISABLE_DOCTOR: 10,
  DELETE_PATIENT: 11,
  DELETE_COMPANY: 12,
};

const getStatusColor = (status: number) => {
  switch(status) {
    case Constants.USER.VERIFIED.PENDING:
      return { className: 'pending', label: 'Sin validar' };
    case Constants.USER.VERIFIED.VERIFIED:
      return { className: 'active', label: 'Aprobado' };
    case Constants.USER.VERIFIED.REJECTED:
      return { className: 'rejected', label: 'Rechazado' };
    case Constants.USER.VERIFIED.IN_PROCESS:
      return { className: 'pending', label: 'Pendiente' };
    default:
      return { className: 'rejected', label: 'No definido' };
  }
}

const initialForm = {
  search: '',
  date: '',
  level_id: Constants.LEVELS.PATIENT,
};

class Users extends React.Component {

  state = {
    modal: MODAL.NONE,
    selectedItem: null,
    form: { ...initialForm },
    data: [],
    pagination: {
      pages: 1,
      page: 1,
    },
  }

  debounce?: NodeJS.Timeout;

  componentDidMount(): void {
    this.getUsers();
  }

  getUsers = async (page = 1) => {
    try {
      const res = await UsersService.get({ page, ...this.state.form });
      const { data, ...rest } = res as any;

      this.setState({
        data: data,
        pagination: rest,
      });

    } catch (error) {
      Globals.showError('Ocurrió un error inesperado');
    }
  }

  download = async () => {
    try {
      const res: any = await UsersService.get({ download: true, ...this.state.form });
      if (!res?.url) return;

      const link = document.createElement('a');
      link.href = Globals.fromStorage(res?.url);
      link.download = 'reporte.xlsx';
      link.target = '_blank';
      link.click();

    } catch (error) {
      Globals.showError('Ocurrió un error inesperado');
    }
  }

  change = async (value: string | number | boolean, target: keyof typeof initialForm) => {
    const form = Globals.clone(this.state.form);
    form[target] = value;
    await this.setState({ form });

    if (target === 'level_id') {
      await this.setState({
        data: [],
        pagination: {
          page: 1,
          pages: 1,
        },
      });
      this.getUsers();

    } else {
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => this.getUsers(), 800);
    }
  }

  cleanFilters = async () => {
    await this.setState((prev: any) => ({
      form: { ...initialForm, level_id: prev.form.level_id },
    }));

    this.getUsers();
  }

  setModal = (modal: number, item?: any) => {
    this.setState({ modal, selectedItem: item ?? null });
  }

  canCleanFilters = () => {
    if (this.state.form.search !== initialForm.search)
      return true;

    if (this.state.form.date !== initialForm.date)
      return true;

    return false
  }

  render() {

    const { form, modal } = this.state;
    const canCleanFilters = this.canCleanFilters();

    return (
      <div id="users">
        <Modal
          id="users-view-patient"
          show={modal === MODAL.VIEW_PATIENT}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalViewPatient
            user={this.state.selectedItem}
            onClose={() => this.setModal(MODAL.NONE)}
            onEdit={() => this.setModal(MODAL.EDIT_PATIENT, this.state.selectedItem)}
          />
        </Modal>

        <Modal
          id="users-view-doctor"
          size="lg"
          show={modal === MODAL.VIEW_DOCTOR}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalViewDoctor
            user={this.state.selectedItem}
            onClose={() => this.setModal(MODAL.NONE)}
            onEdit={() => this.setModal(MODAL.EDIT_DOCTOR, this.state.selectedItem)}
            onApprove={() => this.setModal(MODAL.ACCEPT_DOCTOR, this.state.selectedItem)}
            onReject={() => this.setModal(MODAL.REJECT_DOCTOR, this.state.selectedItem)}
          />
        </Modal>

        <Modal
          id="users-view-company"
          show={modal === MODAL.VIEW_COMPANY}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalViewCompany
            user={this.state.selectedItem}
            onClose={() => this.setModal(MODAL.NONE)}
            onEdit={() => this.setModal(MODAL.EDIT_COMPANY, this.state.selectedItem)}
          />
        </Modal>

        <Modal
          id="users-edit-patient"
          show={modal === MODAL.EDIT_PATIENT}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalEditPatient
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-edit-doctor"
          show={modal === MODAL.EDIT_DOCTOR}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalEditDoctor
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-edit-company"
          show={modal === MODAL.EDIT_COMPANY}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalEditCompany
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-approve-doctor"
          show={modal === MODAL.ACCEPT_DOCTOR}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalApproveDoctor
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-reject-doctor"
          show={modal === MODAL.REJECT_DOCTOR}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalRejectDoctor
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-enable-doctor"
          show={modal === MODAL.ENABLE_DOCTOR}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalEnableDoctor
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-disable-doctor"
          show={modal === MODAL.DISABLE_DOCTOR}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalDisableDoctor
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-delete-patient"
          show={modal === MODAL.DELETE_PATIENT}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalDeletePatient
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <Modal
          id="users-delete-company"
          show={modal === MODAL.DELETE_COMPANY}
          onHide={() => this.setModal(MODAL.NONE)}
        >
          <ModalDeleteCompany
            user={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getUsers();
            }}
          />
        </Modal>

        <section className="custom-filters">
          <FilterTable
            label="Paciente"
            iconActive={PatientActiveIcon}
            iconInactive={PatientInactiveIcon}
            onChange={() => this.change(Constants.LEVELS.PATIENT, 'level_id')}
            isActive={form.level_id === Constants.LEVELS.PATIENT}
          />
          <FilterTable
            label="Médico"
            iconActive={DoctorActiveIcon}
            iconInactive={DoctorInactiveIcon}
            onChange={() => this.change(Constants.LEVELS.DOCTOR, 'level_id')}
            isActive={form.level_id === Constants.LEVELS.DOCTOR}
          />
          <FilterTable
            label="Empresa"
            iconActive={CompanyActiveIcon}
            iconInactive={CompanyInactiveIcon}
            onChange={() => this.change(Constants.LEVELS.COMPANY, 'level_id')}
            isActive={form.level_id === Constants.LEVELS.COMPANY}
          />
        </section>

        <section className="filters">
          <div className="main-filter">
            {form.level_id === Constants.LEVELS.PATIENT && (
              <Input
                className="filter-x2"
                label="Buscar"
                placeholder="Nombre del paciente, cédula"
                value={form.search}
                onChange={(e: IonChangeEvent) => this.change(e.target.value, 'search')}
              />
            )}
            {form.level_id === Constants.LEVELS.DOCTOR && (
              <Input
                className="filter-x2"
                label="Buscar"
                placeholder="Nombre del médico, cédula"
                value={form.search}
                onChange={(e: IonChangeEvent) => this.change(e.target.value, 'search')}
              />
            )}
            {form.level_id === Constants.LEVELS.COMPANY && (
              <Input
                className="filter-x2"
                label="Buscar"
                placeholder="Nombre Empresa, Persona contacto"
                value={form.search}
                onChange={(e: IonChangeEvent) => this.change(e.target.value, 'search')}
              />
            )}
          </div>
          <Input
            label="Fecha de registro"
            type="date"
            value={form.date}
            onChange={(e: IonChangeEvent) => this.change(e.target.value, 'date')}
          />
          {canCleanFilters && (
            <Button clear onClick={this.cleanFilters}>
              Limpiar
            </Button>
          )}
        </section>

        {form.level_id === Constants.LEVELS.PATIENT && (
          <div className="rg-table">
            <div className="rg-table-header">
              <p>Usuarios Registrados</p>
              <div className="rg-table-actions">
                <button onClick={this.download}>
                  <img src={Download2Icon} alt="Descargar" />
                  <span>Descargar Excel</span>
                </button>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Fecha de registro</th>
                  <th>Nombre</th>
                  <th>C.I.</th>
                  <th>Teléfono</th>
                  <th>Correo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {this.state.data.map((user: any, index: number) => (
                  <tr key={`patient-${index}`}>
                    <td>{ moment(user.created_at).format('DD-MM-YYYY') }</td>
                    <td>{ user?.full_name }</td>
                    <td>{ user?.full_document }</td>
                    <td>{ user?.full_phone }</td>
                    <td>{ user?.email }</td>
                    <td>
                      <div className="actions">
                        <img
                          src={ViewIcon}
                          alt="Ver"
                          title="Ver"
                          onClick={() => this.setModal(MODAL.VIEW_PATIENT, user)}
                        />
                        <img
                          src={EditIcon}
                          alt="Editar"
                          title="Editar"
                          onClick={() => this.setModal(MODAL.EDIT_PATIENT, user)}
                        />
                        <span title="Eliminar" onClick={() => this.setModal(MODAL.DELETE_PATIENT, user)}>
                          Eliminar
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {form.level_id === Constants.LEVELS.DOCTOR && (
          <div className="rg-table">
            <div className="rg-table-header">
              <p>Usuarios Registrados</p>
              <div className="rg-table-actions">
                <button onClick={this.download}>
                  <img src={Download2Icon} alt="Descargar" />
                  <span>Descargar Excel</span>
                </button>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Fecha de registro</th>
                  <th>Nombre</th>
                  <th>C.I.</th>
                  <th>Teléfono</th>
                  <th>Correo</th>
                  <th>Dirección</th>
                  <th>Estatus</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {this.state.data.map((user: any, index: number) => {
                  const status = getStatusColor(user?.verified);
                  const canEnable = user?.status === Constants.USER.STATUS.INACTIVE && user?.verified === Constants.USER.VERIFIED.VERIFIED;
                  const canDisable = user?.status === Constants.USER.STATUS.ACTIVE && user?.verified === Constants.USER.VERIFIED.VERIFIED;

                  return (
                    <tr key={`doctor-${index}`}>
                      <td>{ moment(user?.created_at).format('DD-MM-YYYY') }</td>
                      <td>{ user?.full_name }</td>
                      <td>{ user?.full_document }</td>
                      <td>{ user?.full_phone }</td>
                      <td>{ user?.email }</td>
                      <td>{ user?.address }</td>
                      <td>
                        {user?.status === Constants.USER.STATUS.INACTIVE ? (
                          <span className="rejected">Inhabilitado</span>
                        ) : (
                          <span className={status?.className}>{ status?.label }</span>
                        )}
                      </td>
                      <td>
                        <div className="actions">
                          {canEnable && (
                            <img
                              src={ShieldIcon}
                              alt="Habilitar"
                              title="Habilitar"
                              onClick={() => this.setModal(MODAL.ENABLE_DOCTOR, user)}
                            />
                          )}
                          {canDisable && (
                            <img
                              src={LockIcon}
                              alt="Inhabilitar"
                              title="Inhabilitar"
                              onClick={() => this.setModal(MODAL.DISABLE_DOCTOR, user)}
                            />
                          )}
                          <img
                            src={ViewIcon}
                            alt="Ver"
                            title="Ver"
                            onClick={() => this.setModal(MODAL.VIEW_DOCTOR, user)}
                          />
                          {!canEnable && user.verified === Constants.USER.VERIFIED.VERIFIED && (
                            <img
                              src={EditIcon}
                              alt="Editar"
                              title="Editar"
                              onClick={() =>this.setModal(MODAL.EDIT_DOCTOR, user)}
                            />
                          )}
                          {/* {user.verified === Constants.USER.VERIFIED.PENDING && (
                            <>
                              <img
                                src={CheckWhiteIcon}
                                alt="Aceptar"
                                title="Aceptar"
                                onClick={() => this.setModal(MODAL.ACCEPT_DOCTOR, user)}
                              />
                              <img
                                src={CloseIcon}
                                alt="Rechazar"
                                title="Rechazar"
                                onClick={() => this.setModal(MODAL.REJECT_DOCTOR, user)}
                              />
                            </>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {form.level_id === Constants.LEVELS.COMPANY && (
          <div className="rg-table">
            <div className="rg-table-header">
              <p>Usuarios Registrados</p>
              <div className="rg-table-actions">
                <button onClick={this.download}>
                  <img src={Download2Icon} alt="Descargar" />
                  <span>Descargar Excel</span>
                </button>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Fecha de registro</th>
                  <th>Nombre</th>
                  <th>RIF</th>
                  <th>Teléfono</th>
                  <th>Correo</th>
                  <th>Dirección</th>
                  <th>Persona de contacto</th>
                  <th>Teléfono de contacto</th>
                  <th>Correo de contacto</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {this.state.data.map((user: any, index: number) => (
                  <tr key={`company-${index}`}>
                    <td>{ moment(user.created_at).format('DD-MM-YYYY') }</td>
                    <td>{ user?.company_name }</td>
                    <td>{ user?.company_full_document }</td>
                    <td>{ user?.company_full_phone }</td>
                    <td>{ user?.company_email }</td>
                    <td>{ user?.address }</td>
                    <td>{ user?.full_name }</td>
                    <td>{ user?.full_phone }</td>
                    <td>{ user?.email }</td>
                    <td>
                      <div className="actions">
                        <img
                          src={ViewIcon}
                          alt="Ver"
                          title="Ver"
                          onClick={() => this.setModal(MODAL.VIEW_COMPANY, user)}
                        />
                        <img
                          src={EditIcon}
                          alt="Editar"
                          title="Editar"
                          onClick={() => this.setModal(MODAL.EDIT_COMPANY, user)}
                        />
                        <span title="Eliminar" onClick={() => this.setModal(MODAL.DELETE_COMPANY, user)}>
                          Eliminar
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          pages={this.state.pagination.pages}
          active={this.state.pagination.page}
          onChange={this.getUsers}
        />
      </div>
    )
  }
}

interface FilterTableProps {
  label: string;
  iconActive: string;
  iconInactive: string;
  onChange: () => void;
  isActive: boolean;
}

const FilterTable = ({ label, iconActive, iconInactive, onChange, isActive }: FilterTableProps) => {
  return (
    <div className={classes('filter-tab', isActive && 'active')} onClick={onChange}>
      <img src={isActive ? iconActive : iconInactive} alt={label} />
      <span>{ label }</span>
    </div>
  );
}

export default Users;